import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { RolePermission } from '../../types/admin';
import { Save, Check, X } from 'lucide-react';

interface RolesPermissionsMatrixProps {
  rolesPermissions: RolePermission[];
  onUpdate: (roleId: string, permissions: Record<string, boolean>) => Promise<unknown>;
}

const PERMISSION_LABELS: Record<string, string> = {
  complaint_create: 'Create Complaint',
  complaint_view: 'View Complaint',
  complaint_update: 'Update Complaint',
  complaint_assign: 'Assign Complaint',
  complaint_close: 'Close Complaint',
  workorder_create: 'Create Work Order',
  workorder_assign: 'Assign Work Order',
  workorder_complete: 'Complete Work Order',
  vendor_create: 'Create Vendor',
  vendor_update: 'Update Vendor',
  admin_config: 'Admin Config',
  admin_users: 'Admin Users',
  reports_view: 'View Reports',
};

export const RolesPermissionsMatrix: React.FC<RolesPermissionsMatrixProps> = ({ rolesPermissions, onUpdate }) => {
  const [editing, setEditing] = useState<Record<string, Record<string, boolean>>>({});

  const permissionKeys = Object.keys(PERMISSION_LABELS);

  const handleToggle = (roleId: string, permKey: string) => {
    setEditing(prev => {
      const current = prev[roleId] || rolesPermissions.find(r => r.role_id === roleId)?.permissions || {};
      return { ...prev, [roleId]: { ...current, [permKey]: !current[permKey] } };
    });
  };

  const handleSave = async (roleId: string) => {
    if (editing[roleId]) {
      await onUpdate(roleId, editing[roleId]);
      setEditing(prev => { const next = { ...prev }; delete next[roleId]; return next; });
    }
  };

  const handleCancel = (roleId: string) => {
    setEditing(prev => { const next = { ...prev }; delete next[roleId]; return next; });
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left py-3 px-3 font-semibold text-slate-400 sticky left-0 bg-slate-900 z-10">Permission</th>
              {rolesPermissions.map(rp => (
                <th key={rp.role_id} className="text-center py-3 px-3 font-semibold text-slate-400 min-w-[80px]">
                  {rp.role_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permissionKeys.map(permKey => (
              <tr key={permKey} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                <td className="py-2.5 px-3 text-slate-300 sticky left-0 bg-slate-900 z-10">{PERMISSION_LABELS[permKey]}</td>
                {rolesPermissions.map(rp => {
                  const perms = editing[rp.role_id] || rp.permissions;
                  const isEnabled = perms[permKey] || false;
                  return (
                    <td key={rp.role_id} className="py-2.5 px-3 text-center">
                      <button
                        onClick={() => handleToggle(rp.role_id, permKey)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                          isEnabled
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-800 text-slate-500 border border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {isEnabled ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {Object.keys(editing).length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
          <span className="text-xs text-slate-500">Unsaved changes:</span>
          {Object.keys(editing).map(roleId => {
            const role = rolesPermissions.find(r => r.role_id === roleId);
            return (
              <div key={roleId} className="flex items-center gap-1">
                <span className="text-xs text-slate-400">{role?.role_name}</span>
                <Button variant="primary" size="sm" onClick={() => handleSave(roleId)} icon={<Save className="w-3 h-3" />}>Save</Button>
                <Button variant="ghost" size="sm" onClick={() => handleCancel(roleId)} icon={<X className="w-3 h-3" />}>Cancel</Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
